import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';

import { useBrand } from '../../context/BrandContext';
import { useAuth } from '../../context/AuthContext';

const OAuthCallback = () => {
  const { platform } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleOAuthCallback } = useBrand();
  const { user } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('');
  // The callback code and state are single-use on the server. handleOAuthCallback
  // changes identity whenever BrandContext re-renders (e.g. after its own
  // getBrand refresh), which re-runs this effect and would post the same code
  // again, so only the first run may call the server.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const processCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state');
        const brandId = searchParams.get('brand_id') || localStorage.getItem('selectedBrandId');

        if (error) {
          setStatus('error');
          setMessage(`OAuth error: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received');
          return;
        }

        if (!brandId) {
          setStatus('error');
          setMessage('No brand ID found. Please select a brand first.');
          return;
        }

        setStatus('processing');
        setMessage('Connecting your account...');

        const result = await handleOAuthCallback(platform, code, parseInt(brandId), state);
        
        if (result.success) {
          setStatus('success');
          setMessage(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected successfully!`);
          
          // Return to the connections page after 2 seconds
          setTimeout(() => {
            navigate(`/brands/${brandId}/integrations`);
          }, 2000);
        } else {
          setStatus('error');
          setMessage(result.error || 'Failed to connect account');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred while connecting your account');
      }
    };

    processCallback();
  }, [platform, searchParams, handleOAuthCallback, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <CircularProgress size={24} />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'info';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Box sx={{ mb: 3 }}>
            {getStatusIcon()}
          </Box>
          
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {status === 'processing' && 'Connecting Account'}
            {status === 'success' && 'Account Connected!'}
            {status === 'error' && 'Connection Failed'}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {message}
          </Typography>

          {status === 'error' && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/brands')}
                sx={{ mr: 2 }}
              >
                Go to Brands
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </Box>
          )}

          {status === 'success' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Redirecting to social accounts page...
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OAuthCallback;
